import {createAction, props} from "@ngrx/store";
import {Filter} from "../../../../models/filter";
import {GenerateGroupNameModel} from "../models/generate-group-name.model";
import {VerifyGroupNameModel} from "../models/verify-group-name.model";
import {AddGroupDetailsModel} from "../models/add-group-details.model";
import {FetchGroupDetailsModel} from "../models/fetch-group-details.model";
import {UpdateGroupDetailsModel} from "../models/update-group-details.model";
import {FetchClientFilterModel} from "../models/fetch-client-filter.model";
import {MetricOptionModel} from "../../../../models/metric-option.model";
import {FetchSessionListModel} from "../models/fetch-session-list.model";
import {FetchCoachesFilterModel} from "../models/fetch-coaches-filter.model";
import {FetchClientDetailsModel} from "../models/fetch-client-details.model";

/******** CLEAR STATE ***********/
const CLEAR_STATE = '[group details] clear state';
export const ClearState = createAction(CLEAR_STATE);

/******** FETCH CLIENT FILTER ***********/
const FETCH_CLIENT_FILTER_REQUEST = '[group details] fetch client filter requested';
export const FetchClientFilterRequest = createAction(FETCH_CLIENT_FILTER_REQUEST, props<{
  payload?: FetchClientFilterModel['request']
}>());

const FETCH_CLIENT_FILTER_SUCCESS = '[group details] fetch client filter successful';
export const FetchClientFilterSuccess = createAction(FETCH_CLIENT_FILTER_SUCCESS, props<{
  pageNumber: number,
  clientFilter: Filter,
  message: string
}>());

const FETCH_CLIENT_FILTER_FAILURE = '[group details] fetch client filter failure';
export const FetchClientFilterFailure = createAction(FETCH_CLIENT_FILTER_FAILURE);

/******** FETCH GROUPS STATUS FILTER ***********/
const FETCH_GROUP_STATUS_FILTER_REQUEST = '[group details] fetch group status filter requested';
export const FetchGroupStatusFilterRequest = createAction(FETCH_GROUP_STATUS_FILTER_REQUEST);

const FETCH_GROUP_STATUS_FILTER_SUCCESS = '[group details] fetch group status filter successful';
export const FetchGroupStatusFilterSuccess = createAction(FETCH_GROUP_STATUS_FILTER_SUCCESS, props<{
  groupStatusFilter: Filter,
  message: string
}>());

const FETCH_GROUP_STATUS_FILTER_FAILURE = '[group details] fetch group status filter failure';
export const FetchGroupStatusFilterFailure = createAction(FETCH_GROUP_STATUS_FILTER_FAILURE);

/******** FETCH MEASUREMENTS FILTER ***********/
const FETCH_MEASUREMENTS_FILTER_REQUEST = '[group details] fetch measurements filter requested';
export const FetchMeasurementsFilterRequest = createAction(FETCH_MEASUREMENTS_FILTER_REQUEST);

const FETCH_MEASUREMENTS_FILTER_SUCCESS = '[group details] fetch measurements filter successful';
export const FetchMeasurementsFilterSuccess = createAction(FETCH_MEASUREMENTS_FILTER_SUCCESS, props<{
  measurementsFilter: {
    list: MetricOptionModel[],
    total: number,
    pageNumber: number,
    limit: number,
    hasNext: boolean,
    hasPrev: boolean,
    loading?: boolean
  },
  message: string
}>());

const FETCH_MEASUREMENTS_FILTER_FAILURE = '[group details] fetch measurements filter failure';
export const FetchMeasurementsFilterFailure = createAction(FETCH_MEASUREMENTS_FILTER_FAILURE);

/******** FETCH COACHES FILTER ***********/
const FETCH_COACHES_FILTER_REQUEST = '[group details] fetch coaches filter requested';
export const FetchCoachesFilterRequest = createAction(FETCH_COACHES_FILTER_REQUEST, props<{
  payload: FetchCoachesFilterModel['request']
}>());

const FETCH_COACHES_FILTER_SUCCESS = '[group details] fetch coaches filter successful';
export const FetchCoachesFilterSuccess = createAction(FETCH_COACHES_FILTER_SUCCESS, props<{
  coachesFilter: Filter,
  message: string
}>());

const FETCH_COACHES_FILTER_FAILURE = '[group details] fetch coaches filter failure';
export const FetchCoachesFilterFailure = createAction(FETCH_COACHES_FILTER_FAILURE);

/******** AUTO GENERATE GROUP NAME ***********/
const GENERATE_GROUP_NAME_REQUEST = '[group details] auto generate group name requested';
export const GenerateGroupNameRequest = createAction(GENERATE_GROUP_NAME_REQUEST, props<{
  payload: GenerateGroupNameModel['request']
}>());
const GENERATE_GROUP_NAME_SUCCESS = '[group details] auto generate group name successful';
export const GenerateGroupNameSuccess = createAction(GENERATE_GROUP_NAME_SUCCESS, props<{
  name: string,
  message: string
}>());
const GENERATE_GROUP_NAME_FAILURE = '[group details] auto generate group name failure';
export const GenerateGroupNameFailure = createAction(GENERATE_GROUP_NAME_FAILURE);

/******** CLEAR AUTO GENERATED GROUP NAME ***********/
const CLEAR_AUTO_GENERATED_GROUP_NAME = '[group details] clear auto generated group name';
export const ClearAutoGeneratedGroupName = createAction(CLEAR_AUTO_GENERATED_GROUP_NAME);

/******** VERIFY GROUP NAME ***********/
const VERIFY_GROUP_NAME_REQUEST = '[group details] verify group name requested';
export const VerifyGroupNameRequest = createAction(VERIFY_GROUP_NAME_REQUEST, props<{
  payload: VerifyGroupNameModel['request'],
}>());
const VERIFY_GROUP_NAME_SUCCESS = '[group details] verify group name successful';
export const VerifyGroupNameSuccess = createAction(VERIFY_GROUP_NAME_SUCCESS, props<{
  isAvailable: boolean,
  message: string
}>());
const VERIFY_GROUP_NAME_FAILURE = '[group details] verify group name failure';
export const VerifyGroupNameFailure = createAction(VERIFY_GROUP_NAME_FAILURE);

const CLEAR_VERIFY_GROUP_NAME = '[group details] clear verify group name';
export const ClearGroupName = createAction(CLEAR_VERIFY_GROUP_NAME);

/*********** ADD GROUP DETAILS **************/
const ADD_GROUP_DETAILS_REQUEST = '[group details] add group details requested';
export const AddGroupDetailsRequest = createAction(ADD_GROUP_DETAILS_REQUEST, props<{
  payload: AddGroupDetailsModel['request'],
}>());

const ADD_GROUP_DETAILS_SUCCESS = '[group details] add group details successful';
export const AddGroupDetailsSuccess = createAction(ADD_GROUP_DETAILS_SUCCESS, props<{
  groupDetails: AddGroupDetailsModel['response'],
  message: string
}>());

const ADD_GROUP_DETAILS_FAILURE = '[group details] add group details failure';
export const AddGroupDetailsFailure = createAction(ADD_GROUP_DETAILS_FAILURE);

/*********** UPDATE GROUP DETAILS **************/
const UPDATE_GROUP_DETAILS_REQUEST = '[group details] update group details requested';
export const UpdateGroupDetailsRequest = createAction(UPDATE_GROUP_DETAILS_REQUEST, props<{
  payload: UpdateGroupDetailsModel['request'],
}>());

const UPDATE_GROUP_DETAILS_SUCCESS = '[group details] update group details successful';
export const UpdateGroupDetailsSuccess = createAction(UPDATE_GROUP_DETAILS_SUCCESS, props<{
  groupDetails: UpdateGroupDetailsModel['response'],
  message: string
}>());

const UPDATE_GROUP_DETAILS_FAILURE = '[group details] update group details failure';
export const UpdateGroupDetailsFailure = createAction(UPDATE_GROUP_DETAILS_FAILURE);

/*********** FETCH GROUP DETAILS **************/
const FETCH_GROUP_DETAILS_REQUEST = '[group details] fetch group details requested';
export const FetchGroupDetailsRequest = createAction(FETCH_GROUP_DETAILS_REQUEST, props<{
  payload: FetchGroupDetailsModel['request']
}>());

const FETCH_GROUP_DETAILS_SUCCESS = '[group details] fetch group details successful';
export const FetchGroupDetailsSuccess = createAction(FETCH_GROUP_DETAILS_SUCCESS, props<{
  groupDetails: AddGroupDetailsModel['response'],
  message: string
}>());

const FETCH_GROUP_DETAILS_FAILURE = '[group details] fetch group details failure';
export const FetchGroupDetailsFailure = createAction(FETCH_GROUP_DETAILS_FAILURE);


/*********** FETCH SESSIONS LIST **************/
const FETCH_SESSIONS_LIST_REQUEST = '[group details] fetch sessions list requested';
export const FetchSessionListRequest = createAction(FETCH_SESSIONS_LIST_REQUEST);

const FETCH_SESSIONS_LIST_SUCCESS = '[group details] fetch sessions list successful';
export const FetchSessionListSuccess = createAction(FETCH_SESSIONS_LIST_SUCCESS, props<{
  sessionList: FetchSessionListModel['response'],
  message: string
}>());

const FETCH_SESSIONS_LIST_FAILURE = '[group details] fetch sessions list failure';
export const FetchSessionListFailure = createAction(FETCH_SESSIONS_LIST_FAILURE);

/*********** CREATE GROUP **************/
const CREATE_GROUP_REQUEST = '[group details] create group requested';
export const CreateGroupRequest = createAction(CREATE_GROUP_REQUEST, props<{
  payload: any;
  role: number
}>());

const CREATE_GROUP_SUCCESS = '[group details] create group successful';
export const CreateGroupSuccess = createAction(CREATE_GROUP_SUCCESS, props<{
  message: string
}>());

const CREATE_GROUP_FAILURE = '[group details] create group failure';
export const CreateGroupFailure = createAction(CREATE_GROUP_FAILURE);

/*********** SET DUPLICATE GROUP **************/
const SET_DUPLICATE_GROUP_REQUEST = '[group details] set duplicate group request';
export const SetDuplicateGroupRequest = createAction(SET_DUPLICATE_GROUP_REQUEST, props<{
  payload: FetchGroupDetailsModel['request']
}>());

const SET_DUPLICATE_GROUP_SUCCESS = '[group details] set duplicate group successful';
export const SetDuplicateGroupSuccess = createAction(SET_DUPLICATE_GROUP_SUCCESS, props<{
  groupDetails: AddGroupDetailsModel['response'],
  message: string
}>());

const SET_DUPLICATE_GROUP_FAILURE = '[group details] set duplicate group failure';
export const SetDuplicateGroupFailure = createAction(SET_DUPLICATE_GROUP_FAILURE);

/*********** CLEAR DUPLICATE GROUP **************/
const CLEAR_DUPLICATE_GROUP = '[group details] clear duplicate group';
export const ClearDuplicateGroup = createAction(CLEAR_DUPLICATE_GROUP);

/*********** FETCH GROUP NAME **************/
const FETCH_GROUP_NAME_REQUEST = '[group details] fetch group name requested';
export const FetchGroupNameRequest = createAction(FETCH_GROUP_NAME_REQUEST, props<{
  payload: FetchGroupDetailsModel['request']
}>());

const FETCH_GROUP_NAME_SUCCESS = '[group details] fetch group name successful';
export const FetchGroupNameSuccess = createAction(FETCH_GROUP_NAME_SUCCESS, props<{
  name: string,
  startDate: string,
  message: string
}>());

const FETCH_GROUP_NAME_FAILURE = '[group details] fetch group name failure';
export const FetchGroupNameFailure = createAction(FETCH_GROUP_NAME_FAILURE);

/******** FETCH CLIENT DETAILS ***********/
const FETCH_CLIENT_DETAILS_REQUEST = '[group details] fetch client details requested';
export const FetchClientDetailsRequest = createAction(FETCH_CLIENT_DETAILS_REQUEST, props<{
  payload?: FetchClientDetailsModel['request']
}>());

const FETCH_CLIENT_DETAILS_SUCCESS = '[group details] fetch client details successful';
export const FetchClientDetailsSuccess = createAction(FETCH_CLIENT_DETAILS_SUCCESS, props<{
  clientDetails: FetchClientDetailsModel['response'],
}>());

const FETCH_CLIENT_DETAILS_FAILURE = '[group details] fetch client details failure';
export const FetchClientDetailsFailure = createAction(FETCH_CLIENT_DETAILS_FAILURE);

const CLEAR_CLIENT_DETAILS = '[group details] clear client details';
export const ClearClientDetails = createAction(CLEAR_CLIENT_DETAILS);

/*********** DELETE GROUP **************/
const DELETE_GROUP_REQUEST = '[group details] delete group requested';
export const DeleteGroupRequest = createAction(DELETE_GROUP_REQUEST, props<any>());

const DELETE_GROUP_SUCCESS = '[group details] delete group successful';
export const DeleteGroupSuccess = createAction(DELETE_GROUP_SUCCESS, props<{
  message: string, refresh?: boolean
}>());

const DELETE_GROUP_FAILURE = '[group details] delete group failure';
export const DeleteGroupFailure = createAction(DELETE_GROUP_FAILURE);
